import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  useCancelCustomAgentRunMutation,
  useDeleteCustomAgentConfigMutation,
  useDispatchCustomAgentRunMutation,
  useGetCustomAgentConfigEvaluationQuery,
  useGetCustomAgentConfigQuery,
  useGetCustomAgentTriggerStateQuery,
  useListCustomAgentRunsQuery,
  useUpdateCustomAgentTriggerOccurrenceMutation,
  useUpdateCustomAgentConfigMutation,
  type UpdateCustomAgentConfigPayload
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import { NativeSelect, NativeSelectOption } from '../../../components/ui/native-select'
import { RequestAccessPanel, ResourceShareButton } from '../../../components/ResourceSharingDialog'
import { StatusPill } from '../../../components/primitives/StatusPill'
import { describeCron } from '../../../components/cron'
import { CustomAgentResultsBrowser } from '../../../components/custom-agents/CustomAgentResultsBrowser'
import { CustomAgentTriggersForm } from '../../../components/custom-agents/CustomAgentTriggersForm'
import { ConfirmDialog } from '../components/DeleteConfirmDialog'
import { DetailError, DetailLoading, InlineAlert } from '../components/DetailStates'
import { DetailHeader } from '../components/DetailHeader'
import { InfoStrip } from '../components/InfoStrip'
import { ConfigLabelsSection } from '../components/ConfigLabelsSection'
import { RunsLinkTable } from '../components/RunsLinkTable'
import { WritebackToolsSection } from './WritebackToolsSection'
import { CustomAgentNotificationsSection } from './CustomAgentNotificationsSection'
import { proxyApiErrorMessage, Section } from './config-detail-chrome'
import { EquipmentScope } from '../components/EquipmentScope'
import {
  indexTriggerEvals,
  TriggerRulesEditor
} from '../../../components/triggers/TriggerRulesEditor'
import { StoredTriggerStateList } from '../../../components/triggers/StoredTriggerStateList'
import { customAgentTitleStatus, formatDate } from '../util/format'
import {
  deriveCustomAgentLiveSummary,
  type CustomAgentLiveSummary
} from './custom-agent-live-summary'
import { tasksHref } from '../../../lib/component-links'

interface CustomAgentConfigDetailProps {
  id: string
}

function isAccessDeniedStatus(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('status' in error)) return false
  const status = (error as { status: unknown }).status
  return status === 404 || status === 403
}

/**
 * Detail view for one custom agent. The mission and output schema are authored
 * through the AI builder (chat sidebar). This page covers operations
 * (run/pause/delete), optional notification delivery, run history, and the
 * schema-aware results browser.
 */
const CustomAgentConfigDetail = ({ id }: CustomAgentConfigDetailProps) => {
  const navigate = useNavigate()
  const [actionError, setActionError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [editingTriggers, setEditingTriggers] = useState(false)
  const [triggersError, setTriggersError] = useState<string | null>(null)

  const {
    data: config,
    isLoading,
    error,
    refetch
  } = useGetCustomAgentConfigQuery(id, {
    skip: !id,
    pollingInterval: 5000,
    skipPollingIfUnfocused: true
  })
  const {
    data: triggerState,
    isLoading: triggerStateLoading,
    isError: triggerStateError
  } = useGetCustomAgentTriggerStateQuery(
    { customAgentConfigId: id },
    { skip: !id || editingTriggers, pollingInterval: 5000, skipPollingIfUnfocused: true }
  )
  const { data: evaluation } = useGetCustomAgentConfigEvaluationQuery(
    { customAgentConfigId: id },
    { skip: !id || editingTriggers, pollingInterval: 5000, skipPollingIfUnfocused: true }
  )
  const evalByTriggerId = useMemo(
    () => indexTriggerEvals(evaluation?.triggers),
    [evaluation?.triggers]
  )
  const { data: runs = [], isLoading: runsLoading } = useListCustomAgentRunsQuery(
    { custom_agent_config_id: id },
    { skip: !id }
  )
  const [updateConfig, { isLoading: updating }] = useUpdateCustomAgentConfigMutation()
  const [deleteConfig, { isLoading: deleting }] = useDeleteCustomAgentConfigMutation()
  const [dispatchRun, { isLoading: dispatching }] = useDispatchCustomAgentRunMutation()
  const [cancelRun] = useCancelCustomAgentRunMutation()
  const [updateOccurrence, { isLoading: updatingOccurrence }] =
    useUpdateCustomAgentTriggerOccurrenceMutation()

  const liveSummary = useMemo(() => {
    if (!config) return null
    return deriveCustomAgentLiveSummary({
      triggers: config.triggers,
      storedTriggerStates: triggerState?.triggers,
      configState: config.state,
      evalLoading: triggerStateLoading,
      evalError: triggerStateError,
      hasEvaluation: false,
      gateOnUnreviewedFindings: config.gate_on_unreviewed_findings === true,
      unreviewedFindingsCount: config.unreviewed_findings_count ?? 0
    })
  }, [config, triggerState, triggerStateLoading, triggerStateError])

  const goBack = () => void navigate('/configure?type=custom')

  if (isLoading) return <DetailLoading message="Loading custom agent…" />
  if (isAccessDeniedStatus(error)) {
    return (
      <RequestAccessPanel
        resourceType="custom_agent_config"
        id={id}
        onAccessGranted={() => {
          void refetch()
        }}
        notFoundMessage="Failed to load custom agent."
      />
    )
  }
  if (error || !config) return <DetailError message="Failed to load custom agent." />

  const builtIn = Boolean(config.system_key)
  const unreviewedGateOn = config.gate_on_unreviewed_findings === true
  const runConcurrency = config.run_concurrency ?? 'exclusive'
  const titleStatus = customAgentTitleStatus({
    state: config.state,
    gateOnUnreviewedFindings: unreviewedGateOn,
    unreviewedFindingsCount: config.unreviewed_findings_count ?? 0
  })

  const handleDispatch = async () => {
    if (config.state !== 'active') return
    try {
      setActionError(null)
      await dispatchRun({ configId: config.id }).unwrap()
    } catch (e) {
      setActionError(proxyApiErrorMessage(e, 'Failed to dispatch custom-agent run.'))
    }
  }

  const togglePause = async () => {
    try {
      setActionError(null)
      const nextState = config.state === 'paused' ? 'active' : 'paused'
      await updateConfig({
        id: config.id,
        patch: { state: nextState }
      }).unwrap()
    } catch (e) {
      setActionError(proxyApiErrorMessage(e, 'Failed to update agent state.'))
    }
  }

  const toggleUnreviewedGate = async () => {
    try {
      setActionError(null)
      await updateConfig({
        id: config.id,
        patch: { gate_on_unreviewed_findings: !unreviewedGateOn }
      }).unwrap()
    } catch (e) {
      setActionError(proxyApiErrorMessage(e, 'Failed to update unreviewed-findings gate.'))
    }
  }

  const changeRunConcurrency = async (next: 'exclusive' | 'parallel') => {
    try {
      setActionError(null)
      await updateConfig({
        id: config.id,
        patch: { run_concurrency: next }
      }).unwrap()
    } catch (e) {
      setActionError(proxyApiErrorMessage(e, 'Failed to update run concurrency.'))
    }
  }

  const doDelete = async () => {
    try {
      setActionError(null)
      await deleteConfig(id).unwrap()
      setDeleteOpen(false)
      goBack()
    } catch (e) {
      setActionError(proxyApiErrorMessage(e, 'Failed to delete agent.'))
      setDeleteOpen(false)
    }
  }

  const saveTriggers = async (patch: UpdateCustomAgentConfigPayload) => {
    try {
      setTriggersError(null)
      await updateConfig({ id: config.id, patch }).unwrap()
      setEditingTriggers(false)
    } catch (e) {
      // Stay in edit mode so the operator can fix the trigger the proxy
      // rejected (unresolvable tag, tags spanning two gateways, …).
      setTriggersError(proxyApiErrorMessage(e, 'Failed to save triggers.'))
    }
  }

  const handleOccurrenceAction = (
    occurrenceId: string,
    action: 'acknowledge' | 'suppress' | 'unsuppress'
  ) => {
    setActionError(null)
    void updateOccurrence({
      customAgentConfigId: config.id,
      occurrenceId,
      action
    })
      .unwrap()
      .catch((error: unknown) =>
        setActionError(proxyApiErrorMessage(error, 'Failed to update trigger occurrence.'))
      )
  }

  return (
    <div className="flex flex-col gap-6">
      <DetailHeader
        title={config.name}
        backLabel="Custom agents"
        onBack={goBack}
        trailing={
          <div className="flex items-center gap-2">
            {builtIn ? <StatusPill status="info">Built-in</StatusPill> : null}
            <StatusPill status={titleStatus.status}>{titleStatus.label}</StatusPill>
          </div>
        }
        actions={
          builtIn ? null : (
            <>
              <ResourceShareButton
                resourceName={config.name}
                resourceLabel="custom agent"
                accessScopeId={config.access_scope_id}
                scopeDescription="Sharing applies to this configuration and all of its outputs."
              />
              <Button
                size="sm"
                onClick={() => void handleDispatch()}
                disabled={config.state !== 'active' || dispatching || updating}
                title={
                  config.state === 'active'
                    ? 'Schedule an immediate run.'
                    : 'Resume the agent to run it manually.'
                }
              >
                {dispatching ? 'Dispatching…' : 'Run now'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void togglePause()}
                disabled={updating || dispatching}
              >
                {config.state === 'paused' ? 'Resume' : 'Pause'}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeleteOpen(true)}
                disabled={deleting}
              >
                Delete
              </Button>
            </>
          )
        }
      />

      {liveSummary && (
        <ConfigLiveSummaryBanner
          summary={liveSummary}
          onDisableGate={
            liveSummary.kind === 'gated_unreviewed_findings'
              ? () => void toggleUnreviewedGate()
              : undefined
          }
          disableGateBusy={updating}
        />
      )}

      {actionError && <InlineAlert kind="down">{actionError}</InlineAlert>}

      {(config.triggers?.length ?? 0) > 0 && (
        <section
          aria-labelledby="live-trigger-events-heading"
          className="flex flex-col gap-3 rounded-xl border border-line bg-card p-4 shadow-sm"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 id="live-trigger-events-heading" className="m-0 text-sm font-semibold text-ink">
                Live trigger events
              </h2>
              <p className="mt-0.5 m-0 text-xs text-ink-4">
                Active and pending activation cycles are shown first.
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-st-run-bg px-2.5 py-1 text-11 font-medium text-st-run">
              <span className="size-1.5 animate-pulse rounded-full bg-st-run" aria-hidden />
              Updates every 5s
            </span>
          </div>
          {triggerState ? (
            <StoredTriggerStateList
              triggers={config.triggers}
              states={triggerState.triggers}
              occurrences={triggerState.occurrences}
              actionBusy={updatingOccurrence}
              onOccurrenceAction={handleOccurrenceAction}
            />
          ) : (
            <p className="m-0 text-sm text-ink-3">
              Stored state is unavailable until the poller evaluates these triggers.
            </p>
          )}
        </section>
      )}

      {unreviewedGateOn && config.has_reviewable_findings === false ? (
        <InlineAlert kind="warn">
          <span className="font-medium">Nothing to review</span>
          {
            ' — this agent is set to wait for a human review, but its output schema has no reviewable fields. Automatic runs will never be blocked by review. '
          }
          <button
            type="button"
            className="cursor-pointer border-0 bg-transparent p-0 font-medium text-brand hover:underline"
            onClick={() => void toggleUnreviewedGate()}
            disabled={updating}
          >
            Allow automatic runs
          </button>
        </InlineAlert>
      ) : null}

      {config.usage_description && (
        <p className="m-0 text-sm leading-relaxed text-ink-2">{config.usage_description}</p>
      )}

      {!builtIn ? (
        <>
          <ConfigLabelsSection
            labels={config.labels ?? []}
            onChange={async (next) => {
              try {
                setActionError(null)
                await updateConfig({
                  id: config.id,
                  patch: { labels: next }
                }).unwrap()
              } catch (e) {
                setActionError(proxyApiErrorMessage(e, 'Failed to update labels.'))
                throw e
              }
            }}
          />

          <CustomAgentNotificationsSection
            config={config}
            busy={updating}
            onSave={async (patch) => {
              try {
                setActionError(null)
                await updateConfig({
                  id: config.id,
                  patch
                }).unwrap()
              } catch (e) {
                setActionError(proxyApiErrorMessage(e, 'Failed to update notifications.'))
                throw e
              }
            }}
          />

          <Section
            label="Run concurrency"
            hint="Choose whether this agent can have overlapping pending or running analyses."
          >
            <div className="flex max-w-2xl flex-col gap-3">
              <label className="flex flex-col gap-1 text-sm font-medium text-ink-2">
                Run concurrency
                <NativeSelect
                  className="w-full"
                  value={runConcurrency}
                  onChange={(event) =>
                    void changeRunConcurrency(event.target.value as 'exclusive' | 'parallel')
                  }
                  disabled={updating}
                >
                  <NativeSelectOption value="exclusive">
                    Exclusive — one run at a time
                  </NativeSelectOption>
                  <NativeSelectOption value="parallel">
                    Parallel — allow overlapping runs
                  </NativeSelectOption>
                </NativeSelect>
              </label>
              <p className="m-0 text-xs leading-relaxed text-ink-3">
                Condition cooldowns, unreviewed-findings gates, and organization limits still apply
                in either mode.
              </p>
              {runConcurrency === 'parallel' ? (
                <InlineAlert kind="warn">
                  Parallel runs can overlap across Chat, Run now, invokes, schedules, and triggers.
                  This uses more model capacity and may increase cost.
                </InlineAlert>
              ) : null}
            </div>
          </Section>
        </>
      ) : null}

      <InfoStrip
        items={[
          {
            label: 'Cadence',
            value: config.cron ? describeCron(config.cron).long : 'No schedule'
          },
          {
            label: 'Triggers',
            value:
              (config.triggers?.length ?? 0) === 0
                ? 'None'
                : `${config.triggers.length} condition trigger${config.triggers.length === 1 ? '' : 's'}`
          },
          {
            label: 'Trigger events',
            value:
              config.trigger_events == null
                ? 'Legacy level dispatch'
                : config.trigger_events.length
                  ? config.trigger_events.join(', ')
                  : 'No edge notifications'
          },
          {
            label: 'Unreviewed gate',
            value: unreviewedGateOn ? 'On' : 'Off'
          },
          {
            label: 'Run concurrency',
            value: runConcurrency === 'parallel' ? 'Parallel' : 'Exclusive'
          },
          { label: 'Updated', value: formatDate(config.updated_at) }
        ]}
      />

      {!builtIn ? (
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void toggleUnreviewedGate()}
            disabled={updating}
          >
            {unreviewedGateOn
              ? 'Allow automatic runs while findings are unreviewed'
              : 'Block automatic runs while findings are unreviewed'}
          </Button>
        </div>
      ) : null}

      <Section
        label="Mission"
        hint={
          builtIn
            ? "Platform-managed mission used for chat invocations. Built-in agents can't be edited."
            : "The agent's prompt — what it investigates and computes each run. Edit it by asking the AI in the chat sidebar."
        }
      >
        <pre className="m-0 whitespace-pre-wrap rounded-xl border border-line bg-card px-4 py-3 font-sans text-sm leading-relaxed text-ink-2 shadow-sm">
          {config.prompt}
        </pre>
      </Section>

      <Section
        label="Output schema"
        hint="Every run's result document is validated against this JSON Schema. Immutable after creation."
      >
        <pre className="m-0 max-h-72 overflow-auto rounded-xl border border-line bg-card px-4 py-3 text-xs leading-relaxed text-ink-2 shadow-sm">
          {JSON.stringify(config.output_schema, null, 2)}
        </pre>
      </Section>

      {config.parameters_schema ? (
        <Section
          label="Invocation parameters"
          hint="Structured inputs Chat must supply before this agent can run."
        >
          <pre className="m-0 max-h-72 overflow-auto rounded-xl border border-line bg-card px-4 py-3 text-xs leading-relaxed text-ink-2 shadow-sm">
            {JSON.stringify(config.parameters_schema, null, 2)}
          </pre>
        </Section>
      ) : null}

      <Section
        label="Writeback tools"
        hint="Parameterized DB writes this agent may call as db_write_<name>. Ask the assistant in chat to add one. Unbind does not affect in-flight runs."
      >
        <WritebackToolsSection
          configId={config.id}
          tools={config.writeback_tools ?? []}
          onError={(message) => setActionError(message)}
        />
      </Section>

      <Section label="Watching" hint="Equipment and signals this agent reads from.">
        <EquipmentScope tagPaths={config.tag_paths ?? []} />
      </Section>

      <Section
        label="When this happens, investigate"
        hint={
          editingTriggers
            ? 'Pick tags from the registry, choose a comparison or aggregation, and set thresholds. Changes apply on Save.'
            : 'Every trigger is tracked independently; all active triggers count.'
        }
        actions={
          editingTriggers || builtIn ? null : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setTriggersError(null)
                setEditingTriggers(true)
              }}
              disabled={updating}
            >
              Edit
            </Button>
          )
        }
      >
        {editingTriggers ? (
          <CustomAgentTriggersForm
            initialValue={config}
            busy={updating}
            error={triggersError}
            onSubmit={saveTriggers}
            onCancel={() => {
              setTriggersError(null)
              setEditingTriggers(false)
            }}
          />
        ) : (
          <TriggerRulesEditor
            triggers={config.triggers}
            tagPaths={config.tag_paths ?? []}
            evalByTriggerId={evalByTriggerId}
            readOnly
            onChange={() => {
              /* view-only */
            }}
          />
        )}
      </Section>

      <Section
        label="Results"
        hint="Schema-validated result documents from the agent's timeseries. Select a row for a structured view; open the run for the full report and tool trace."
      >
        <CustomAgentResultsBrowser configId={config.id} outputSchema={config.output_schema} />
      </Section>

      <Section
        label="Runs"
        hint="Scheduled and triggered runs are dispatched by the proxy. Use 'Run now' above to fire one manually. Click any row to open the run."
      >
        <RunsLinkTable
          rows={runs.map((r) => ({
            id: r.id,
            state: r.state,
            createdAt: r.created_at,
            followUpActive: Boolean(r.active_follow_up_run_id)
          }))}
          type="custom"
          isLoading={runsLoading}
          emptyMessage="No runs yet."
          agentLabel="Custom-agent"
          onCancel={async (runId) => {
            await cancelRun(runId).unwrap()
          }}
        />
      </Section>

      {!builtIn ? (
        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete this custom agent?"
          description="This cannot be undone. Existing runs and results remain but no new runs will fire."
          confirmLabel="Delete"
          destructive
          onConfirm={doDelete}
          busy={deleting}
        />
      ) : null}
    </div>
  )
}

function ConfigLiveSummaryBanner({
  summary,
  onDisableGate,
  disableGateBusy
}: {
  summary: CustomAgentLiveSummary
  onDisableGate?: () => void
  disableGateBusy?: boolean
}) {
  switch (summary.kind) {
    case 'gated_unreviewed_findings': {
      const count = summary.unreviewedFindingsCount ?? 0
      return (
        <InlineAlert kind="warn">
          <span className="font-medium">Waiting on review</span>
          {` — ${count} finding${count === 1 ? '' : 's'} still awaiting review. Automatic runs will resume after they are approved or rejected. `}
          <Link to={tasksHref()} className="font-medium text-brand hover:underline">
            Review findings
          </Link>
          {onDisableGate ? (
            <>
              {' · '}
              <button
                type="button"
                className="cursor-pointer border-0 bg-transparent p-0 font-medium text-brand hover:underline"
                onClick={onDisableGate}
                disabled={disableGateBusy}
              >
                Allow automatic runs
              </button>
            </>
          ) : null}
        </InlineAlert>
      )
    }
    case 'triggering': {
      const count = summary.activeTriggerCount ?? 0
      const labels = summary.activeTriggerLabels ?? []
      return (
        <InlineAlert kind="down">
          <span className="font-medium">Active</span>
          {count > 0
            ? ` — ${count} trigger${count === 1 ? '' : 's'} active${labels.length ? `: ${labels.join(', ')}` : ''}.`
            : ' — a trigger condition is active.'}
        </InlineAlert>
      )
    }
    case 'condition_met_inactive':
      return (
        <InlineAlert kind="warn">
          <span className="font-medium">Condition met — config inactive</span>
          {summary.activeTriggerLabels?.length
            ? ` (${summary.activeTriggerLabels.join(', ')}). Resume the agent to allow dispatch.`
            : '. Resume the agent to allow dispatch.'}
        </InlineAlert>
      )
    case 'healthy':
      return (
        <InlineAlert kind="info" className="border-st-run/40 bg-st-run-bg text-st-run">
          <span className="font-medium">Healthy</span> — no trigger conditions are met.
        </InlineAlert>
      )
    case 'checking':
      return (
        <InlineAlert kind="info">
          <span className="font-medium">Checking trigger status…</span>
        </InlineAlert>
      )
    case 'unavailable':
      return (
        <InlineAlert kind="warn">
          <span className="font-medium">Status unavailable</span> — live evaluation could not be
          determined.
        </InlineAlert>
      )
    case 'no_triggers':
      return (
        <InlineAlert kind="info">
          <span className="font-medium">No triggers configured</span>
        </InlineAlert>
      )
  }
}

export default CustomAgentConfigDetail
